import { arrayBufferToBase64String, base64StringToArrayBuffer } from '@app/utils/base64'

import type { ApiDocumentDocumentType, ApiEncryptionKey } from '@app/packages/api/v2/types/entities'

export async function createPostDocumentsData(file: File, documentType: ApiDocumentDocumentType, keyData: ApiEncryptionKey): Promise<FormData> {
  const dek = crypto.getRandomValues(new Uint8Array(32))
  const iv = crypto.getRandomValues(new Uint8Array(12))

  // Read file and encrypt with AES-256-GCM
  const fileBuffer = await file.arrayBuffer()
  const key = await crypto.subtle.importKey('raw', dek, { name: 'AES-GCM' }, false, ['encrypt'])

  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: new Uint8Array(0), tagLength: 128 }, key, fileBuffer)

  const tagLength = 16
  const ciphertext = encryptedData.slice(0, encryptedData.byteLength - tagLength)
  const authTag = encryptedData.slice(encryptedData.byteLength - tagLength)

  // Encrypt DEK with RSA-OAEP-SHA256
  const publicKey = await crypto.subtle.importKey(
    'spki',
    base64StringToArrayBuffer(pemToBase64(keyData.attributes.public_key)),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  )

  const encryptedDek = arrayBufferToBase64String(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, dek))
  const encryptedFile = new Blob([ciphertext], { type: 'application/octet-stream' })

  const formData = new FormData()
  formData.append('document_type', documentType)
  formData.append('key_id', keyData.attributes.key_id)
  formData.append('encrypted_file', encryptedFile, file.name)
  formData.append('encrypted_dek', encryptedDek)

  // Send encryption_metadata as nested form fields (not JSON string!)
  formData.append('encryption_metadata[algorithm]', 'AES-256-GCM')
  formData.append('encryption_metadata[parameters][iv]', arrayBufferToBase64String(iv.buffer))
  formData.append('encryption_metadata[parameters][auth_tag]', arrayBufferToBase64String(authTag))

  return formData
}

function pemToBase64(pem: string) {
  return pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s/g, '')
}
